/** Thin fetch wrapper. Always sends cookies (same-origin) and parses JSON. */

export interface Me {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
}

export interface DriveItem {
  id: string;
  name: string;
  mimeType: string;
  isFolder: boolean;
  modifiedTime?: string;
  size?: number;
  iconLink?: string;
}

export interface DriveMeta extends DriveItem {
  parents?: string[];
  webViewLink?: string;
  path: { id: string; name: string }[];
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  me: () => request<Me>('/api/me'),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  folders: (parentId?: string) =>
    request<{ items: DriveItem[] }>(`/api/folders${parentId ? `/${parentId}` : ''}`),
  fileMeta: (fileId: string) => request<DriveMeta>(`/api/file/${fileId}/meta`),
  contentUrl: (fileId: string) => `/api/file/${fileId}/content`,
  fileText: async (fileId: string) => {
    const res = await fetch(`/api/file/${fileId}/content`, { credentials: 'same-origin' });
    if (!res.ok) throw new ApiError(res.status, res.statusText);
    return res.text();
  },
};
